/**
 * AP-36 3.5 und 4.1 — Anzeige am Feld und der schlichte Abschnitt, der die
 * Karte mit Stift ersetzt.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import type { Status } from './hooks'

/**
 * Eingabefeld, das gesperrt wie Text aussieht (4.1) — so bleibt die Seite ohne
 * zweite Darstellung lesbar.
 */
export const feldKlasse = `${inlineInput} disabled:cursor-default disabled:border-transparent disabled:bg-transparent disabled:px-0 disabled:text-foreground disabled:opacity-100 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/40`

/**
 * Ein **leeres** Datumsfeld ohne die Vorlage des Browsers („tt.mm.jjjj") — es sieht aus wie jedes andere leere
 * Feld (Auftraggeber 6.10.2026). Beim Bearbeiten steht die Vorlage wieder da, sonst wüsste man nicht, was zu
 * tippen ist. Als Klasse zum Datumsfeld: `className={`${feldKlasse} ${datumOhneVorlage(feld.props.value)}`}`.
 *
 * Wirkt in Chrome, Edge und Safari (`::-webkit-datetime-edit`); Firefox kennt den Baustein nicht und zeigt
 * die Vorlage weiter.
 */
export const datumOhneVorlage = (wert: string | null | undefined): string => (wert ? '' : '[&:not(:focus)::-webkit-datetime-edit]:text-transparent')

/** Kreis erst nach 300 ms (schnelle Antworten flackern nicht), Häkchen 1,5 s. */
export function FeldStatusZeichen({ status }: { status: Status }) {
  const [sichtbar, setSichtbar] = useState(false)
  useEffect(() => {
    if (status.zustand !== 'speichert') {
      setSichtbar(false)
      return
    }
    const t = setTimeout(() => setSichtbar(true), 300)
    return () => clearTimeout(t)
  }, [status.zustand])
  if (status.zustand === 'speichert' && sichtbar)
    return <Loader2 aria-hidden className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
  if (status.zustand === 'gespeichert') return <Check aria-hidden className="h-3.5 w-3.5 shrink-0 text-green-600" />
  return null
}

/**
 * Zustand und Fehler am Feld: Kreis bzw. Häkchen, bei Fehler die Meldung, „Erneut"
 * und der Hinweis auf Esc. Vorleser hören „gespeichert" bzw. den Fehler (7.5).
 */
export function FeldStatus({ status, zeichen = true }: { status: Status; zeichen?: boolean }) {
  const ansage = status.zustand === 'gespeichert' ? 'gespeichert' : status.fehler ? `Fehler: ${status.fehler}` : ''
  return (
    <>
      {zeichen && <FeldStatusZeichen status={status} />}
      <span className="sr-only" aria-live="polite">
        {ansage}
      </span>
      {status.fehler && (
        <span className="mt-0.5 block text-xs text-destructive">
          {status.fehler}
          {status.erneut && (
            <button type="button" className="ml-2 underline" onClick={status.erneut}>
              Erneut
            </button>
          )}
          <span className="ml-2 text-muted-foreground">Esc stellt den alten Wert her.</span>
        </span>
      )}
    </>
  )
}

/** Abschnitt mit Titel und Rahmen — Inhalt sind immer die Felder (4.1). */
export function Abschnitt({
  titel,
  aktionen,
  unten,
  className = '',
  children,
}: {
  titel: ReactNode
  /** Rechts neben dem Titel (z. B. ein Link), nie ein Stift. */
  aktionen?: ReactNode
  unten?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <section className={`${formCardSm} ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <h3 className={cardTitle}>{titel}</h3>
        {aktionen}
      </div>
      <div className="mt-3 space-y-2">{children}</div>
      {unten}
    </section>
  )
}

/**
 * Eine Zeile im Abschnitt: Beschriftung links, Feld rechts, darunter Hilfstext
 * (etwa der geerbte Wert, 4.1) und Zustand.
 */
export function FeldZeile({
  label,
  status,
  hilfe,
  children,
}: {
  label: ReactNode
  status?: Status
  hilfe?: ReactNode
  children: ReactNode
}) {
  return (
    <label className="flex flex-col gap-1 text-sm sm:flex-row sm:items-start sm:gap-3">
      <span className="shrink-0 pt-1.5 text-muted-foreground sm:w-40">{label}</span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="min-w-0 flex-1">{children}</span>
          {status && <FeldStatusZeichen status={status} />}
        </span>
        {hilfe && <span className="mt-0.5 block text-xs text-muted-foreground">{hilfe}</span>}
        {status && <FeldStatus status={status} zeichen={false} />}
      </span>
    </label>
  )
}

/**
 * Kontrollkästchen mit Beschriftung, Hinweis und Zustand — speichert sofort (3.2).
 * `aus` zeigt es ungehakt, obwohl gespeichert „an" steht (Baustein nicht gebucht:
 * gespeichert bleibt es, angeboten wird es nicht).
 */
export function SchalterZeile({
  feld,
  label,
  hinweis,
  aus,
  einzug = 0,
  unten,
}: {
  feld: { schalter: { checked: boolean; disabled?: boolean; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; name: string; ref: (el: HTMLElement | null) => void } } & Status
  label: ReactNode
  hinweis?: ReactNode
  aus?: boolean
  einzug?: 0 | 1 | 2
  unten?: ReactNode
}) {
  const gesperrt = feld.schalter.disabled || aus
  return (
    <div className={einzug === 2 ? 'ml-14' : einzug === 1 ? 'ml-7' : ''}>
      <label className={`flex items-start gap-3 ${gesperrt ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          {...feld.schalter}
          checked={aus ? false : feld.schalter.checked}
          disabled={gesperrt || undefined}
          className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-ring disabled:cursor-not-allowed"
        />
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            {label}
            <FeldStatusZeichen status={feld} />
          </span>
          {hinweis && <span className="mt-0.5 block text-xs text-muted-foreground">{hinweis}</span>}
        </span>
      </label>
      <div className="ml-7">
        <FeldStatus status={feld} zeichen={false} />
      </div>
      {unten}
    </div>
  )
}
